import React, { useMemo, useState } from 'react'
import { graphql, useFragment } from '../../gql'
import { useGQLInfinite } from '../../hooks/GQLUtilityHooks'
import { Button } from '../../components/shadcn/Button'
import { OrderReturnsTable } from './components/OrderReturnsTable'
import { ActiveReturnsTable } from './components/ActiveReturnsTable'
import { CompletedReturnsTable } from './components/CompletedReturnsTable'
import SerialNumbersModal from '../../components/SerialNumbersModal'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowRotateLeft, faPencil, faSave } from '@fortawesome/pro-solid-svg-icons'
import ReturnAllModal from './components/ReturnAllModal'
import { EditReturnItemModal, ReturnFormData } from './components/EditReturnItemModal'
import { useOrderDetailContext } from './OrderDetailContext'
import { useReturns, useReturnQuantities, useReturnBarcode, useReturnAll } from './lib/hooks'

export const OrderReturns_OrderFragment = graphql(`
  fragment OrderReturns_OrderFragment on Order {
    id
    reservationNumber
    processStatus {
      id
      name
    }
    checkout {
      id
      returnsDepot {
        id
        name
      }
      depots {
        id
        name
      }
    }
    items {
      id
      type
      quantity
      product {
        id
        name
        requiredSerialNumbers
        ...ProductCell_ProductFragment
      }
      clearanceItems {
        id
        amount
        position1
        position2
        position3
        batch
        expirationDate
        product {
          id
          name
          code
          ean
          ean2
          ean3
          ean4
          ean5
          requiredSerialNumbers
          ...ProductCell_ProductFragment
        }
        productDepots {
          depot {
            id
          }
        }
      }
      ...OrderReturnsTable_OrderItemFragment
    }
  }
`)

const getProductMovePacksQuery = graphql(`
  query GetProductMovePacks($orderId: Int!) {
    productMovePacks(filters: { order: $orderId, type: "in" }) {
      items {
        id
        moves {
          serialNumbers
          amount
          orderItem {
            id
          }
          product {
            id
          }
        }
        ...CompletedReturnsTable_ProductMovePackFragment
      }
    }
  }
`)

export const OrderReturns: React.FC = () => {
  const context = useOrderDetailContext()
  const order = useFragment(OrderReturns_OrderFragment, context.order)

  // Initialize hooks
  const { activeReturns, isCreatingReturn, addReturn, removeReturn, processReturns, serialNumbersModalProps } =
    useReturns({
      orderId: order.id,
      orderProcessStatusId: order.processStatus.id,
    })

  // Fetch existing return packs for this order
  const { data: orderPMPs } = useGQLInfinite(getProductMovePacksQuery, ['productMovePacks', { orderId: order.id }], {
    orderId: order.id,
  })

  const returnedProductMoves = useMemo(() => orderPMPs?.flatMap(pmp => pmp.moves), [orderPMPs])

  const orderItemsForReturns = useMemo(() => {
    const items = order.items || []

    return items.reduce<typeof order.items>((acc, item) => {
      if (item.type !== 'product') {
        return acc
      }

      const clearanceItems = (item.clearanceItems || []).filter(ci => ci?.product?.id)

      if (clearanceItems.length > 0) {
        const rowsByProductId = new Map<
          number,
          { product: (typeof clearanceItems)[number]['product']; quantity: number }
        >()
        clearanceItems.forEach(ci => {
          const row = rowsByProductId.get(ci.product.id)
          if (row) {
            row.quantity += ci.amount || 0
          } else {
            rowsByProductId.set(ci.product.id, { product: ci.product, quantity: ci.amount || 0 })
          }
        })
        rowsByProductId.forEach(({ product, quantity }) => {
          acc.push({
            ...item,
            name: product.name ?? item.name,
            quantity,
            product: product as any,
            clearanceItems: clearanceItems.filter(ci => ci.product.id === product.id),
          })
        })
        return acc
      }

      // Fallback for orders without clearance items (e.g. historical ones):
      // expand virtual products from their current bundle definition (one level only)
      const bundleChildren = item.product?.bundleChildren

      if (bundleChildren && bundleChildren.length > 0) {
        bundleChildren.forEach(child => {
          acc.push({
            ...item,
            name: child.child.name ?? item.name,
            quantity: (item.quantity || 0) * (child.quantity || 0),
            product: { ...child.child, isBundleChild: true } as any,
          })
        })
      } else {
        acc.push(item)
      }

      return acc
    }, [])
  }, [order.items])

  // Calculate quantities
  const { orderItemsAvailableToReturn, orderItemsWithQuantities } = useReturnQuantities({
    orderItems: orderItemsForReturns as any,
    returnedProductMoves,
    activeReturns,
  })

  // Barcode scanning
  const { barcodeModalItem, clearBarcodeItem } = useReturnBarcode({
    orderItemsAvailableToReturn,
  })

  // Handle add return with quantity validation
  const handleAddReturn = async (returnData: ReturnFormData) => {
    const orderItem = orderItemsWithQuantities.find(item => {
      if (item.id !== returnData.orderItemId) return false
      const itemProductId = item.product?.id
      if (!itemProductId || !returnData.productId) return true
      return itemProductId === returnData.productId
    })
    if (!orderItem) return

    await addReturn(returnData, orderItem, orderItem.quantityReturned, orderItem.quantityAvailableToReturn)
  }

  // Return all functionality
  const { returnAllModalProps, handleReturnAllItems, handleReturnAllItemsWithModal } = useReturnAll({
    orderItemsAvailableToReturn,
    defaultDepotId: order.checkout?.depots[0]?.id,
    checkoutDepotId: order.checkout?.depots[0]?.id,
    onAddReturn: handleAddReturn,
  })

  const handleProcessReturns = () => {
    processReturns(order.items, returnedProductMoves)
  }

  // Build product name lookup for child products as well
  const productNames = useMemo(() => {
    const names: Record<number, string> = {}
    order.items.forEach(item => {
      if (item.product) {
        names[item.product.id] = item.product.name
      }
      item.product?.bundleChildren?.forEach(child => {
        if (child.child?.id) {
          names[child.child.id] = child.child.name || item.product?.name || ''
        }
      })
      item.clearanceItems?.forEach(ci => {
        if (ci?.product?.id) {
          names[ci.product.id] = ci.product.name || item.product?.name || ''
        }
      })
    })
    return names
  }, [order.items])

  // Check if returns are allowed for current order status
  const canReturn = ['packed', 'dispatched', 'delivered', 'returned'].includes(order.processStatus.id)

  if (!canReturn) {
    return (
      <div className="text-center py-8 text-gray-500">
        Vratky nejsou dostupné pro objednávky ve stavu "{order.processStatus.name}"
      </div>
    )
  }

  console.log(order.checkout?.depots[0]?.id)

  return (
    <div className="m-4 flex flex-col gap-4">
      <SerialNumbersModal {...serialNumbersModalProps} />
      <ReturnAllModal {...returnAllModalProps} />
      {/* Returnable items section */}
      {orderItemsAvailableToReturn.length > 0 ? (
        <div className="gap-4 flex flex-col">
          <div className="flex justify-between">
            <h2 className="font-bold text-2xl">Dosud nevrácené položky</h2>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleReturnAllItemsWithModal}>
                <FontAwesomeIcon icon={faPencil} />
                Vrátit všechno na novou pozici
              </Button>
              <Button variant="default" size="sm" onClick={() => handleReturnAllItems()}>
                <FontAwesomeIcon icon={faArrowRotateLeft} />
                Vrátit všechno na původní pozici
              </Button>
            </div>
          </div>
          <div className="shadow-lg rounded-xl">
            <OrderReturnsTable
              items={orderItemsAvailableToReturn}
              orderId={order.id}
              defaultDepotId={order.checkout?.depots[0]?.id}
              onAddReturn={handleAddReturn}
              depots={order.checkout?.depots.filter(d => d.id && d.name) as { id: number; name: string }[]}
            />
          </div>
        </div>
      ) : null}

      {/* Active returns section */}
      {activeReturns.length > 0 && (
        <div className="gap-4 flex flex-col">
          <div>
            <h2 className="font-bold text-2xl mb-1">Rozpracovaná vratka</h2>
            <span className="text-sm text-danger">
              Pozor, neuložené změny! Pokud chcete dokincit vratku, zmáčkněte na tlačítko "Vrátit" níže.
            </span>
          </div>
          <div className="shadow-lg rounded-xl">
            <ActiveReturnsTable
              activeReturns={activeReturns.map(r => {
                const orderItem = order.items.find(item => item.id === r.orderItemId)
                const baseProduct = orderItem?.product
                const clearanceProduct =
                  orderItem?.clearanceItems?.find(ci => ci?.product?.id === r.productId)?.product || null
                const bundleChild = baseProduct?.bundleChildren?.find(bc => bc.child?.id === r.productId)?.child || null

                return {
                  ...r,
                  product: (clearanceProduct || bundleChild || baseProduct) as any,
                }
              })}
              productNames={productNames}
              depots={order.checkout?.depots}
              onRemove={removeReturn}
            />
          </div>
          <Button
            disabled={activeReturns.length === 0 || isCreatingReturn}
            onClick={handleProcessReturns}
            className="w-64"
            variant="default">
            <FontAwesomeIcon icon={faSave} />
            Vrátit položky
          </Button>
        </div>
      )}

      {/* Completed returns section */}
      {returnedProductMoves?.length > 0 && (
        <div className="gap-4 flex flex-col">
          <h2 className="font-bold text-2xl">Již vrácené položky</h2>
          {orderPMPs?.map(pack => <CompletedReturnsTable key={pack.id} returnPack={pack} />)}
        </div>
      )}

      {/* Barcode-triggered EditReturnItemModal */}
      {barcodeModalItem && (
        <EditReturnItemModal
          orderItem={barcodeModalItem.item}
          orderId={order.id}
          quantityReturned={barcodeModalItem.quantityReturned}
          defaultDepotId={order.checkout?.depots[0]?.id}
          onConfirm={data => {
            handleAddReturn(data)
            clearBarcodeItem()
          }}
          onClose={clearBarcodeItem}
          isOpen={true}
          depots={order.checkout?.depots.filter(d => d.id && d.name) as { id: number; name: string }[]}
        />
      )}
    </div>
  )
}
